import type { ReactNode } from 'react'
import { Card, ConfidenceTicks, Divider, SimpleGrid, Stack, Tag, Text } from '@grovus/design-system'
import { fill, money, num, percent, ratio } from './format'
import type { Row, SortKey } from './partition'
import { belowFloor, sortRows } from './partition'
import { TagChips } from './TagChips'
import {
  AccountMarks, audienceCell, sortLabels, verdictQualifier, qualifierText, floorStruck, formatLabel, markTally,
  PartialLegend, PausedMark, reasonSentence, VerdictMark, type MarkTally, type ViewContext,
} from './view'

interface Props {
  ctx: ViewContext
  rows: Row[]
  sort: SortKey
  dir: 'asc' | 'desc'
  onOpenCreative: (id: string) => void
}

/**
 * The art area keeps the asset's shape, so a 9:16 reads as vertical before anything else,
 * and is capped so a vertical placeholder does not make the card mostly empty box. The cap
 * goes when ingestion supplies thumbnails and there is something in it worth the height.
 */
const ART_MAX_HEIGHT = 200

function aspect(aspectRatio: string | null): string {
  const [w, h] = (aspectRatio ?? '1:1').split(':').map(Number)
  if (!w || !h) return '1 / 1'
  return `${w} / ${h}`
}

/**
 * A card's six measures, built by the grid rather than by the card, so the grid's legend
 * has seen every mark before it renders (GRO-61).
 */
function cardMeasures(row: Row, ctx: ViewContext, mark: MarkTally): Array<[string, ReactNode]> {
  const { t, lang, currency } = ctx
  return [
    [t.spend, mark.at('spend')(money(row.metrics.spend, currency, lang), row.metrics.incomplete.includes('spend'))],
    [t.convCol, mark.at('conversions')(num(row.metrics.conversions, lang), row.metrics.incomplete.includes('conversions'))],
    [
      t.roasCol,
      mark.at('roas')(
        floorStruck(ratio(row.derived.roas.value, lang), row.derived.roas.value !== null, row, ctx),
        row.derived.roas.partial,
      ),
    ],
    [t.cpaCol, mark.at('cpa')(
      floorStruck(money(row.derived.cpa.value, currency, lang, 2), row.derived.cpa.value !== null, row, ctx),
      row.derived.cpa.partial,
    )],
    [t.hookCol, mark.at('hook')(percent(row.derived.hookRate.value, lang, 1), row.derived.hookRate.partial)],
    [t.holdCol, mark.at('hold')(percent(row.derived.holdRate.value, lang, 1), row.derived.holdRate.partial)],
  ]
}

function CreativeCard({ row, ctx, measures, onOpenCreative }: {
  row: Row; ctx: ViewContext; measures: Array<[string, ReactNode]>; onOpenCreative: (id: string) => void
}) {
  const { t, lang } = ctx
  const insufficient = belowFloor(row, ctx.thresholds)
  const qualifier = verdictQualifier(row, t)

  return (
    <Card
      p="sm"
      className="grv-card-clickable"
      data-testid={`creative-card-${row.id}`}
      tabIndex={0}
      onClick={() => onOpenCreative(row.id)}
      // A card that only answers the mouse is a card half the readers cannot open, so it
      // takes focus and answers the two keys a button answers. It does not claim to be a
      // button: role="button" would make everything inside it presentational, and the six
      // measures, the marks and the verdict are only in this card.
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        onOpenCreative(row.id)
      }}
      style={{ cursor: 'pointer' }}
    >
      <div
        style={{
          position: 'relative',
          aspectRatio: aspect(row.aspectRatio),
          maxHeight: ART_MAX_HEIGHT,
          margin: '0 auto',
          width: '100%',
          background: 'var(--mantine-color-default-hover)',
          // A creative the sample cannot rank is hatched on its leading edge, so the grid
          // says it in the card's own shape and not only in the figures below it.
          borderLeft: insufficient ? '3px solid var(--mantine-color-warn-4)' : undefined,
        }}
        data-insufficient={insufficient ? '' : undefined}
      >
        {row.thumbnailUrl !== null && (
          <img
            src={row.thumbnailUrl}
            loading="lazy"
            alt=""
            data-testid="creative-thumbnail"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <Tag
          tt="none"
          size="sm"
          style={{ position: 'absolute', top: 6, left: 6 }}
          data-testid="grid-format"
        >
          {formatLabel(row.format, t)}
        </Tag>
      </div>

      <Stack gap={6} mt="sm">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text size="md" lineClamp={1}>{row.displayName}</Text>
          <ConfidenceTicks
            conversions={row.metrics.conversions ?? 0}
            thresholds={ctx.thresholds}
            progress
          />
        </div>

        <SimpleGrid cols={3} spacing={6}>
          {measures.map(([label, value]) => (
            <div key={label}>
              <Text size="xs" className="grv-muted">{label}</Text>
              <Text size="sm">{value}</Text>
            </div>
          ))}
        </SimpleGrid>

        <Divider />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <AccountMarks row={row} ctx={ctx} />
          <Text size="xs" className="grv-muted">{fill(t.adsMerged, { n: row.ads.length })}</Text>
        </div>

        <TagChips row={row} ctx={ctx} />
        {row.audienceIds.length > 0 && (
          <Text size="xs" className="grv-muted" data-testid="creative-audience">
            {audienceCell(row, ctx.dimensions.audiences, lang, t)}
          </Text>
        )}
        <PausedMark at={row.pausedSince} t={t} lang={lang} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title={reasonSentence(row, ctx)}>
          <VerdictMark row={row} t={t} />
          {qualifier && <Text size="xs" className="grv-muted">{qualifierText(qualifier)}</Text>}
        </div>
      </Stack>
    </Card>
  )
}

/**
 * The same rows as the table, in the same order.
 *
 * The grid exists for recognition: a buyer knows an asset by its shape before its name.
 */
export function CreativesGrid({ ctx, rows, sort, dir, onOpenCreative }: Props) {
  const sorted = sortRows(rows, sort, dir, sortLabels(ctx))
  const mark = markTally(ctx.t)
  const measures = new Map(sorted.map((row) => {
    mark.lagging('accounts', row, ctx)
    return [row.id, cardMeasures(row, ctx, mark)] as const
  }))

  return (
    <Stack gap="md" data-testid="creatives-grid">
      <PartialLegend kinds={mark.kinds()} t={ctx.t} />
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {sorted.map((row) => (
          <CreativeCard key={row.id} row={row} ctx={ctx} measures={measures.get(row.id)!} onOpenCreative={onOpenCreative} />
        ))}
      </SimpleGrid>
    </Stack>
  )
}
